import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#FC551E', // Primary - Vibrant Orange
      light: '#94AF79', // Primary - 400 (lighter olive)
      dark: '#5C7643', // Primary - 600 (darker olive)
      50: '#F6F9F4',
      100: '#ECF2E6',
      200: '#D9E4CE',
      300: '#A9C191',
      400: '#94AF79',
      500: '#739156',
      600: '#5C7643',
      700: '#4A5E37',
      800: '#3D4C2F',
    },
    secondary: {
      main: '#4A5E37', // Primary - 700 (deep olive)
      light: '#5C7643', // Primary - 600
      dark: '#3D4C2F', // Primary - 800 (darkest olive)
    },
    background: {
      default: '#F6F9F4', // Primary - 50 (very light olive)
      paper: '#ECF2E6', // Primary - 100 (light olive)
    },
    text: {
      primary: '#3D4C2F', // Primary - 800 (darkest for text)
      secondary: '#5C7643', // Primary - 600 (medium for secondary text)
    },
  },
  typography: {
    fontFamily: [
      'Montserrat',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    // Default text style for all regular text
    fontSize: 14,
    // @ts-ignore
    fontWeight: 400,
    fontStyle: 'normal',
    lineHeight: '22px',
    letterSpacing: '0.28px', // 2% of 14px
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#3D4C2F',
      fontFamily: 'Montserrat',
      lineHeight: '22px',
      letterSpacing: '0.28px',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#3D4C2F',
      fontFamily: 'Montserrat',
      lineHeight: '22px',
      letterSpacing: '0.28px',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#3D4C2F',
      fontFamily: 'Montserrat',
      lineHeight: '22px',
      letterSpacing: '0.28px',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#3D4C2F',
      fontFamily: 'Montserrat',
      lineHeight: '22px',
      letterSpacing: '0.28px',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#3D4C2F',
      fontFamily: 'Montserrat',
      lineHeight: '22px',
      letterSpacing: '0.28px',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#3D4C2F',
      fontFamily: 'Montserrat',
      lineHeight: '22px',
      letterSpacing: '0.28px',
    },
    body1: {
      fontSize: '14px',
      lineHeight: '22px',
      color: '#5C7643',
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontStyle: 'normal',
      letterSpacing: '0.28px',
    },
    body2: {
      fontSize: '14px',
      lineHeight: '22px',
      color: '#5C7643',
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontStyle: 'normal',
      letterSpacing: '0.28px',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '14px',
      fontFamily: 'Montserrat',
      lineHeight: '22px',
      letterSpacing: '0.28px',
    },
    caption: {
      fontSize: '14px',
      lineHeight: '22px',
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontStyle: 'normal',
      letterSpacing: '0.28px',
    },
    overline: {
      fontSize: '14px',
      lineHeight: '22px',
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontStyle: 'normal',
      letterSpacing: '0.28px',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '14px',
          fontWeight: 600,
          fontFamily: 'Montserrat',
          lineHeight: '22px',
          letterSpacing: '0.28px',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(115, 145, 86, 0.3)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(115, 145, 86, 0.4)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(115, 145, 86, 0.08)',
          border: '1px solid rgba(115, 145, 86, 0.05)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
          '& .MuiInputBase-input': {
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 400,
            fontStyle: 'normal',
            lineHeight: '22px',
            letterSpacing: '0.28px',
          },
          '& .MuiInputBase-input::placeholder': {
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 400,
            fontStyle: 'normal',
            lineHeight: '22px',
            letterSpacing: '0.28px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Montserrat',
          fontSize: '14px',
          fontWeight: 400,
          fontStyle: 'normal',
          lineHeight: '22px',
          letterSpacing: '0.28px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Montserrat',
          fontSize: '14px',
          fontWeight: 400,
          fontStyle: 'normal',
          lineHeight: '22px',
          letterSpacing: '0.28px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: 'Montserrat',
          fontSize: '14px',
          fontWeight: 400,
          fontStyle: 'normal',
          lineHeight: '22px',
          letterSpacing: '0.28px',
        },
      },
    },
  },
}); 